/**
 * statusMessages.js — RC 1.53 Phase 4.2.1
 *
 * Maps (actionCode, controllerStatus, progressLabel) → calm,
 * human-readable status copy. Backend progress_label (when the
 * canonical envelope carries one) takes precedence; otherwise a
 * curated fallback per phase.
 *
 * Phase 4.2.1 scope: CV variant only. The mapping table is keyed by
 * actionCode so Phase 4.3 can extend it for the other three actions
 * without restructuring.
 */

const CV_VARIANT_COPY = Object.freeze({
  dispatching:  'Preparing your tailored CV…',
  running:      'Tailoring your CV…',
  polling:      'Tailoring your CV…',
  aggregating:  'Analysing the job requirements…',
  hydrating:    'Updating your documents…',
  finalizing:   'Finalising your tailored CV…',
  // RC 1.53 Phase 4.2.1.1 — stabilizing covers the post-success
  // window where finalize-charge, credits refresh, hydration and
  // cache invalidation are landing. The indicator stays mounted so
  // the operator sees one continuous "we're still working" signal.
  stabilizing:  'Saving your tailored CV…',
  complete:     'Tailored CV ready.',
  failed:       'CV generation hit a snag.',
});

const COVER_LETTER_COPY = Object.freeze({
  dispatching:  'Preparing your cover letter…',
  running:      'Drafting your cover letter…',
  polling:      'Drafting your cover letter…',
  aggregating:  'Matching your experience to the role…',
  hydrating:    'Updating your documents…',
  finalizing:   'Finalising your cover letter…',
  // RC 1.53 Phase 4.3A — stabilizing covers the post-success window
  // where the URL write, cache-invalidation, and credits refresh
  // are landing. The indicator stays mounted so the operator sees
  // one continuous "we're still working" signal.
  stabilizing:  'Updating your documents…',
  complete:     'Cover letter ready.',
  failed:       'Cover letter generation hit a snag.',
});

const INTERVIEW_PREP_COPY = Object.freeze({
  dispatching:  'Preparing interview context…',
  running:      'Building interview questions…',
  polling:      'Building interview questions…',
  aggregating:  'Analysing likely interview themes…',
  hydrating:    'Updating your documents…',
  finalizing:   'Preparing strategic guidance…',
  // RC 1.53 Phase 4.3A — stabilizing covers the post-success window
  // where the URL write, cache-invalidation, and credits refresh
  // are landing.
  stabilizing:  'Updating your documents…',
  complete:     'Interview Prep ready.',
  failed:       'Interview Prep hit a snag.',
});

const FALLBACK_COPY = Object.freeze({
  dispatching:  'Preparing…',
  running:      'Working on it…',
  polling:      'Working on it…',
  complete:     'Ready.',
  failed:       'That action failed.',
});

const PER_ACTION = Object.freeze({
  cv_variant:      CV_VARIANT_COPY,
  cover_letter:    COVER_LETTER_COPY,
  interview_prep:  INTERVIEW_PREP_COPY,
  analyse_fit:     FALLBACK_COPY,
});

/**
 * Pick a status message.
 *
 *   getStatusMessage('cv_variant', 'polling',     'aggregating')
 *     → 'Analysing the job requirements…'  (uses progressLabel)
 *
 *   getStatusMessage('cv_variant', 'polling',     null)
 *     → 'Tailoring your CV…'                (falls back to status)
 *
 *   getStatusMessage('cv_variant', 'idle',        null)
 *     → null                                (no banner)
 */
export function getStatusMessage(actionCode, controllerStatus, progressLabel) {
  if (!controllerStatus || controllerStatus === 'idle') return null;
  const table = PER_ACTION[actionCode] || FALLBACK_COPY;
  if (progressLabel && table[progressLabel]) {
    return table[progressLabel];
  }
  return table[controllerStatus] || table.running || null;
}

export const STATUS_MESSAGE_KEYS = Object.freeze({
  CV_VARIANT_COPY,
  COVER_LETTER_COPY,
  INTERVIEW_PREP_COPY,
  FALLBACK_COPY,
});
